import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { compose } from "redux";
import { useDispatch, useSelector } from "react-redux";
import { injectIntl } from "react-intl";
import * as ReducerKeys from "../constants/reducerKeys";
import LoaderModal from "../../../common/LoaderModal";
import { LiveSessionAlert, LiveSessionContent } from "../component";
import injectMultipleReducers from "../../../utils/reducers/injectMultipleReducers";
import injectSaga from "../../../utils/saga/injectSaga";
import { courseReducer, liveSessionReducer, livePageReducer, userReducer } from "../reducer";
import { LiveSessionProvider } from "../context/LiveSessionContext";
import { redirectToCourseLanding } from "../utils/pageRedirect";
import saga from "../saga";
import {
  useCourseActions,
  useLivePageActions,
  useUserDomainActions,
  useLiveSessionDomainActions,
} from "../customHooks";
import {
  setupHoneybadger,
  removeHoneybadger,
} from "../../../../js/utils/errorTracking/honeybadger";

export const LiveSessionRecording = ({ courseId, liveSessionId }) => {
  const {
    loading: courseLoading,
    errorFetching: courseFetchingError,
    course,
  } = useSelector((state) => state[ReducerKeys.COURSE]);

  const {
    loading: liveSessionLoading,
    errorFetching: liveSessionFetchingError,
    liveSession,
    liveChannel,
    recording,
  } = useSelector((state) => state[ReducerKeys.LIVE_SESSION]);

  const { loadingUser, errorFetchingUser, user } = useSelector((state) => state[ReducerKeys.USER]);

  const { loading: livePageLoading } = useSelector((state) => state[ReducerKeys.LIVE_PAGE]);

  const dispatch = useDispatch();
  const { getCourse } = useCourseActions();
  const { getCurrentWithoutRelationshipsUser } = useUserDomainActions({ dispatch });
  const { setLivePageLoading } = useLivePageActions({ dispatch });
  const { getLiveSession } = useLiveSessionDomainActions();

  const leaveLiveStreamingPage = useCallback(() => {
    setLivePageLoading();
    redirectToCourseLanding(courseId, false);
  }, []);

  const showLiveSessionAlert = () =>
    courseFetchingError || liveSessionFetchingError || errorFetchingUser;

  const showLoaderModal = () =>
    courseLoading || liveSessionLoading || loadingUser || livePageLoading;

  const showLiveSessionView = () => course?.id && liveSession?.id && user?.id;

  useEffect(() => {
    setupHoneybadger();
    getCurrentWithoutRelationshipsUser();
    getCourse(courseId);
    getLiveSession(liveSessionId);

    return () => {
      removeHoneybadger();
    };
  }, []);

  return (
    <>
      <LoaderModal isOpen={showLoaderModal()} messageKey="live_sessions.loading_message" />
      {showLiveSessionAlert() && (
        <LiveSessionAlert type="error" message="generic.messages.error_try_again" top={8} />
      )}
      {showLiveSessionView() && (
        <LiveSessionProvider
          value={{
            course,
            liveChannel,
            liveSession,
            recording,
            onLeaveSession: leaveLiveStreamingPage,
          }}
        >
          <LiveSessionContent />
        </LiveSessionProvider>
      )}
    </>
  );
};

const withReducers = injectMultipleReducers([
  { key: ReducerKeys.COURSE, reducer: courseReducer },
  { key: ReducerKeys.LIVE_SESSION, reducer: liveSessionReducer },
  { key: ReducerKeys.LIVE_PAGE, reducer: livePageReducer },
  { key: ReducerKeys.USER, reducer: userReducer },
]);

const withSaga = injectSaga({ key: "course[live]", saga });

export default compose(withReducers, withSaga)(injectIntl(LiveSessionRecording));

LiveSessionRecording.propTypes = {
  courseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  liveSessionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};
